import { setClassName } from '@change-corgi/core/error';
import type { TrackingFn } from '@change-corgi/core-react/tracking';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import type {
	LocalSectionHomepageDataExactQuery,
	LocalSectionHomepageDataExactQueryVariables,
	LocalSectionHomepageDataQuery,
	LocalSectionHomepageDataQueryVariables,
	LocalSectionLocationFragment,
} from './api.graphql';
import { LocalSectionHomepageData, LocalSectionHomepageDataExact } from './api.graphql';
import { buildLocationSlug, sortByCardImageFirst } from './utils';

/** Number of petition cards the local section renders — kept in step with its skeleton. */
export const LOCAL_SECTION_PETITION_COUNT = 3;
/**
 * How many trending petitions we ask for. Deliberately wider than what we render: petitions
 * without a photo get sorted to the back, so a wider pool makes it more likely the rendered
 * cards all have images without ever leaving the section short.
 */
const LOCAL_SECTION_PETITION_FETCH_COUNT = 8;

type LocalData = {
	petitions: LocalSectionLocationFragment['trendingPetitionsConnection']['nodes'];
};

export type LocalSectionPetition = LocalData['petitions'][number];
// doesn't come from the api
type DerivedLocalData = {
	slug: string;
	name: string;
	shortName: string;
	localPageUrl: string;
};

export class LocalSectionDataMissingError extends Error {
	constructor() {
		super('No data returned from local section homepage query');
	}
}
setClassName(LocalSectionDataMissingError, 'LocalSectionDataMissingError');

type GetLocalSectionDataReturn = LocalData & DerivedLocalData;
type Location = {
	city: string | null;
	stateCode: string | null;
	countryCode: string | null;
	postalCode?: string | null;
} | null;

function parseLocalSectionResult(
	localSectionLocation: LocalSectionLocationFragment,
	requestedSlug: string,
	track: TrackingFn,
): GetLocalSectionDataReturn {
	const {
		slug,
		city,
		state,
		trendingPetitionsConnection: { nodes: petitions },
	} = localSectionLocation;

	// the section is a grid of petition cards, so without petitions there is nothing to show
	if (petitions.length === 0) {
		throw new Error('No local petitions');
	}
	// the resolved location can differ from the requested one when the API falls back
	// to the nearest location page, so derive display values from the response
	const name = state ? `${city}, ${state.stateCode}` : city;
	void track('homepage_local_data_loaded', {
		user_location: name,
		slug,
		has_local_page_data: true,
		used_nearest_fallback: slug !== requestedSlug,
	});
	return {
		slug,
		name,
		shortName: city,
		localPageUrl: `/local/${slug}?source_location=homepage_local_section`,
		petitions: sortByCardImageFirst(petitions).slice(0, LOCAL_SECTION_PETITION_COUNT),
	};
}

async function fetchLocalSectionData({
	location,
	utilityContext: {
		gql: { fetch },
	},
	track,
	exact,
}: {
	location: Location;
	utilityContext: UtilityContext;
	track: TrackingFn;
	exact: boolean;
}): Promise<GetLocalSectionDataReturn> {
	try {
		if (!location) {
			throw new Error('No location provided');
		}

		const { city, stateCode, countryCode } = location;
		if (!city || !stateCode || !countryCode) {
			throw new Error('Invalid location data');
		}

		const slug = buildLocationSlug({ city, stateCode, countryCode });

		const { data } = exact
			? await fetch<LocalSectionHomepageDataExactQuery, LocalSectionHomepageDataExactQueryVariables>({
					query: LocalSectionHomepageDataExact,
					variables: { slug, first: LOCAL_SECTION_PETITION_FETCH_COUNT },
					rejectOnError: true,
				})
			: await fetch<LocalSectionHomepageDataQuery, LocalSectionHomepageDataQueryVariables>({
					query: LocalSectionHomepageData,
					// lets the API rank nearby locations against the viewer's own location
					// when their city has no location page of its own
					variables: { slug, postalCode: location.postalCode ?? null, first: LOCAL_SECTION_PETITION_FETCH_COUNT },
					rejectOnError: true,
				});

		if (!data.localSectionLocation) {
			throw new Error('No local section location data');
		}

		return parseLocalSectionResult(data.localSectionLocation, slug, track);
	} catch {
		// we don't care what the error type is
		void track('homepage_local_data_loaded', {
			user_location: undefined,
			slug: undefined,
			has_local_page_data: false,
		});
		throw new LocalSectionDataMissingError();
	}
}

export async function getLocalSectionData(
	location: Location,
	utilityContext: UtilityContext,
	track: TrackingFn,
): Promise<GetLocalSectionDataReturn> {
	return fetchLocalSectionData({ location, utilityContext, track, exact: false });
}

/**
 * Strict variant that does not fall back to the nearest location page — used by the
 * edit-location modal, which must error on cities we have no location page for.
 */
export async function getLocalSectionDataExact(
	location: Location,
	utilityContext: UtilityContext,
	track: TrackingFn,
): Promise<GetLocalSectionDataReturn> {
	return fetchLocalSectionData({ location, utilityContext, track, exact: true });
}

getLocalSectionData.queryKey = 'HOMEPAGE_LOCAL_SECTION_LOCATION_BY_SLUG';
